/**
 * HandDrawnFlowBar.jsx — Horizontal flow bar with the canonical sketch signature.
 *
 * Used in the Outcome Paradox "dissolution hops" to keep every chart on the
 * homepage speaking the same editorial-sketch language as the locked
 * SignatureProofGraph and the FailureRecurrenceGraph.
 *
 * Visual signature:
 *   - Wobble rectangle with seeded per-bar imperfections (4 corners + Bezier edges)
 *   - Pressure-varied diagonal hatch fill (opacity + stroke width variance)
 *   - Scarlet leak indicator at the right edge (the "lost" portion)
 *   - Theme-aware via CSS variables (works in light + dark)
 */
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const SCARLET = '#CD2200';

function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/* ── Build hand-drawn wobble rectangle path (horizontal orientation) ── */
function buildWobbleRect(x, y, w, h, seed) {
  const r = seededRandom(seed);

  const tlLean = (r() - 0.5) * 3;
  const trLean = (r() - 0.5) * 3;
  const blDrift = (r() - 0.5) * 2;
  const brDrift = (r() - 0.5) * 2;
  const topWobble = (r() - 0.5) * 2;
  const bottomWobble = (r() - 0.5) * 2;
  const heightVar = (r() - 0.5) * 2;

  const tl = { x: x + tlLean, y: y + (r() - 0.5) * 1.5 };
  const tr = { x: x + w + trLean - 2, y: y + topWobble };
  const br = { x: x + w + brDrift - 2, y: y + h + heightVar };
  const bl = { x: x + blDrift, y: y + h + bottomWobble };

  const topMidX = (tl.x + tr.x) / 2;
  const bottomMidX = (bl.x + br.x) / 2;
  const leftMidY = (tl.y + bl.y) / 2;
  const rightMidY = (tr.y + br.y) / 2;

  const wob = (r() - 0.5) * 3;

  return `
    M ${tl.x} ${tl.y}
    C ${tl.x + 6} ${tl.y + wob}, ${topMidX - 5} ${tl.y - wob * 0.5}, ${topMidX} ${(tl.y + tr.y) / 2 + wob * 0.3}
    C ${topMidX + 5} ${tr.y + wob * 0.4}, ${tr.x - 6} ${tr.y - wob * 0.3}, ${tr.x} ${tr.y}
    C ${tr.x + wob * 0.5} ${rightMidY - 4}, ${br.x - wob * 0.4} ${rightMidY + 4}, ${br.x} ${br.y}
    C ${bottomMidX + 5} ${br.y + wob * 0.3}, ${bottomMidX - 5} ${bl.y - wob * 0.4}, ${bl.x} ${bl.y}
    C ${bl.x - wob * 0.5} ${leftMidY + 4}, ${tl.x + wob * 0.4} ${leftMidY - 4}, ${tl.x} ${tl.y}
    Z
  `;
}

/* ── Pressure-varied hatch (diagonal) ── */
function buildHatchLines(x, y, w, h, seed) {
  const r = seededRandom(seed + 500);
  const lines = [];
  const baseSpacing = 6;

  let offset = -h;
  while (offset < w + h) {
    const spacing = baseSpacing + (r() - 0.5) * 2.5;
    offset += spacing;

    const startX = x + offset + (r() - 0.5) * 1.5;
    const startY = y - 2 + (r() - 0.5) * 1;
    const endX = startX + h + 4 + (r() - 0.5) * 2;
    const endY = y + h + 2 + (r() - 0.5) * 1;

    const opacity = 0.08 + r() * 0.14;
    const sw = 1 + r() * 0.9;

    lines.push({ x1: startX, y1: startY, x2: endX, y2: endY, opacity, sw });
  }
  return lines;
}

/**
 * @param {number} remainingPct  – 0..100, width filled (money remaining)
 * @param {number} lostPct       – the %age lost at THIS hop (for the leak indicator)
 * @param {number} index         – for stagger animation
 * @param {number} seed          – deterministic wobble
 * @param {string} testId
 */
export default function HandDrawnFlowBar({
  remainingPct,
  lostPct,
  index = 0,
  seed = 101,
  testId,
}) {
  // SVG coordinate space — responsive via viewBox
  const SVG_W = 600;
  const SVG_H = 34;
  const PAD_Y = 4;
  const BAR_H = SVG_H - PAD_Y * 2;
  const PAD_X = 2;
  const filledW = Math.max(4, ((SVG_W - PAD_X * 2) * remainingPct) / 100);

  const barPath = useMemo(
    () => buildWobbleRect(PAD_X, PAD_Y, filledW, BAR_H, seed),
    [filledW, seed]
  );
  const hatchLines = useMemo(
    () => buildHatchLines(PAD_X, PAD_Y, filledW, BAR_H, seed),
    [filledW, seed]
  );
  const clipId = `flow-clip-${seed}-${index}`;

  // Leak indicator position (just past the filled edge)
  const leakX = PAD_X + filledW;
  const leakW = Math.max(8, ((SVG_W - PAD_X * 2) * lostPct) / 100);
  const leakPath = useMemo(
    () => buildWobbleRect(leakX, PAD_Y + BAR_H - 5, Math.min(leakW, SVG_W - leakX - 2), 4, seed + 1000),
    [leakX, leakW, seed]
  );

  const delay = 0.15 + index * 0.08;

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      preserveAspectRatio="none"
      className="w-full h-full overflow-visible"
      data-testid={testId ?? `hand-drawn-flow-bar-${index}`}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={barPath} />
        </clipPath>
      </defs>

      {/* Bar outline — scarlet hand-drawn stroke */}
      <motion.path
        d={barPath}
        fill="none"
        stroke={SCARLET}
        strokeOpacity={0.42}
        strokeWidth={1.4}
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Pressure-varied hatch fill, clipped to bar */}
      <g clipPath={`url(#${clipId})`}>
        {hatchLines.map((line, i) => (
          <motion.line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={SCARLET}
            strokeOpacity={line.opacity}
            strokeWidth={line.sw}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: delay + 0.25 + i * 0.008 }}
          />
        ))}
      </g>

      {/* Leak indicator (the lost portion) — scarlet wobble strip */}
      {leakW > 8 && (
        <motion.path
          d={leakPath}
          fill={SCARLET}
          fillOpacity={0.22}
          stroke={SCARLET}
          strokeOpacity={0.45}
          strokeWidth={1}
          strokeLinejoin="round"
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: delay + 0.4 }}
          style={{ transformOrigin: `${leakX}px ${PAD_Y + BAR_H}px` }}
        />
      )}
    </svg>
  );
}
